import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import OnboardingButtonDock from '../../components/OnboardingButtonDock';
import { showToast } from '../../utils/toastUtils';
import { setCurrentStep } from '../../redux/onboardingReducer';
import './OnboardingPlayground.css';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import AnimatedWaves from '../../components/AnimatedWaves';
import axios from 'axios';

const API_URL = "https://aitaler-backend-production.devops-565.workers.dev/api";
const WEBHOOK_URL = "http://34.100.130.205:80/webhooks/rest/webhook";
const AI_RESPONSE_URL = "https://swar-samwad-2.azurewebsites.net/webhooks/rest/webhook";

const OnboardingPlayground = () => {
  const dispatch = useDispatch();
  const reduxAiTalkerConfig = useSelector(state => state.onboarding?.aiTalkerConfig || {});
  const userIdFromRedux = useSelector(state => state.onboarding?.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');
  const [aiTalkerConfig, setAiTalkerConfig] = useState(reduxAiTalkerConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [isSTTActive, setIsSTTActive] = useState(false);
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const [isRedButtonClicked, setIsRedButtonClicked] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(new Audio());

  const fetchAITalkerConfig = useCallback(async (retryCount = 0) => {
    if (!userId) {
      console.error("User ID not found in Redux store");
      showToast("User not found. Please ensure you're logged in.", "error");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/ai-talker/config`, {
        params: { userId },
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const fetchedConfig = response.data;
      localStorage.setItem('aiTalkerConfig', JSON.stringify(fetchedConfig));
      setAiTalkerConfig(fetchedConfig);
      setError(null);
    } catch (error) {
      console.error('Error fetching AI Talker config:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        if (error.response.status === 403) {
          showToast("Access denied. Please check your authentication and try logging in again.", "error");
        } else {
          showToast(`Server error: ${error.response.status}. ${error.response.data.message || ''}`, "error");
        }
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Error setting up request:', error.message);
        showToast(`Error setting up request: ${error.message}`, "error");
      }
      if (retryCount < 3) {
        setTimeout(() => fetchAITalkerConfig(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, API_URL]);

  useEffect(() => {
    fetchAITalkerConfig();
  }, [fetchAITalkerConfig]);

  useEffect(() => {
    const audio = audioRef.current;
    audio.onplay = () => setIsTTSPlaying(true);
    audio.onended = () => {
      setIsTTSPlaying(false);
      setIsRedButtonClicked(false);
    };
    return () => {
      audio.onplay = null;
      audio.onended = null;
    };
  }, []);

  const initializeRecognition = useCallback(() => {
    if (!('webkitSpeechRecognition' in window)) {
      showToast('Speech recognition is not supported in this browser.', 'error');
      return null;
    }
    const instance = new window.webkitSpeechRecognition();
    instance.continuous = true;
    instance.interimResults = false;
    instance.lang = 'en-IN';
    
    instance.onresult = (event) => {
      const last = event.results.length - 1;
      const text = event.results[last][0].transcript;
      setTranscript(text);
      handleAiResponse(text);
    };

    instance.onerror = (event) => {
      showToast(`Speech recognition error: ${event.error}`, 'error');
      setIsSTTActive(false);
    };

    instance.onend = () => {
      setIsSTTActive(false);
    };

    return instance;
  }, []);

  const handleAiResponse = async (text) => {
    try {
      // First, get the AI text response
      const textPayload = {
        prompt: text,
        verbalyze_api_key: "Verbalyze",
        user_id: "default",
        convo_type: "web",
      };
      const textResponse = await axios.post(AI_RESPONSE_URL, textPayload, {
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        }
      });
      // Extract the AI response text
      const aiResponseText = textResponse.data.response;
      setAiResponse(aiResponseText);

      // Now, send this AI response text back to get the voice response
      // Determine the correct ssmlGender based on the voice name
      let ssmlGender = 'NEUTRAL';
      const voiceName = aiTalkerConfig.voice?.name || '';
      if (voiceName.includes('Male') || voiceName.includes('-A') || voiceName.includes('-B') || voiceName.includes('-D')) {
        ssmlGender = 'MALE';
      } else if (voiceName.includes('Female') || voiceName.includes('-C')) {
        ssmlGender = 'FEMALE';
      }

      const voicePayload = {
        userInput: aiResponseText,
        aiTalkerConfig: {
          ...aiTalkerConfig,
          voice: {
            language: aiTalkerConfig.voice?.language || '',
            name: aiTalkerConfig.voice?.name || '',
            pitch: aiTalkerConfig.voice?.pitch || 0,
            rate: aiTalkerConfig.voice?.rate || 1,
            ssmlGender: ssmlGender
          }
        }
      };
      try {
        const voiceResponse = await axios.post(`${API_URL}/ai-talker/playground`, voicePayload, {
          responseType: 'json',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          }
        });
        if (voiceResponse.data && voiceResponse.data.audioContent) {
        const audioBlob = new Blob([Uint8Array.from(atob(voiceResponse.data.audioContent), c => c.charCodeAt(0))], { type: 'audio/mpeg' });
          const audioUrl = URL.createObjectURL(audioBlob);
          audioRef.current.src = audioUrl;
          audioRef.current.play();
        } else {
          throw new Error('No audio content in the response');
        }

        // Send the AI response text to the webhook
        await sendToWebhook(aiResponseText);
      } catch (error) {
        console.error('Error in voice response:', error);
        if (error.response) {
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          console.error('Response headers:', error.response.headers);
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Error setting up request:', error.message);
        }
        showToast(`Error in AI voice response: ${error.message}`, 'error');
      }
    } catch (error) {
      console.error('Error in AI text response:', error);
      showToast(`Error in AI text response: ${error.message}`, 'error');
    }
  };

  const sendToWebhook = async (aiResponseText) => {
    try {
      const webhookPayload = {
        prompt: aiResponseText,
        verbalyze_api_key: "Verbalyze",
        user_id: "haha",
        convo_type: "chat",
        parameters: {
          temperature: 0.5
        },
        language_code: aiTalkerConfig.voice?.language || 'en-US'
      };

      const response = await axios.post(WEBHOOK_URL, webhookPayload);
    } catch (error) {
      console.error('Webhook error:', error);
    }
  };

  const toggleListening = useCallback(() => {
    if (isSTTActive) {
      // Stop listening
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsSTTActive(false);
      setIsRedButtonClicked(false);
    } else {
      // Start listening
      setIsSTTActive(true);
      setIsRedButtonClicked(true);
      setTranscript('');
      setAiResponse('');
      setError(null);

      if (!recognitionRef.current) {
        recognitionRef.current = initializeRecognition();
      }

      if (recognitionRef.current) {
        recognitionRef.current.start();
      }
    }
  }, [isSTTActive, initializeRecognition]);

  return (
    <Box className="playground-container" sx={{ paddingBottom: '80px',paddingTop:"40px" }}>
      <Typography variant="h4" className="playground-title">
        AI Talker Playground
      </Typography>
      {isLoading ? (
        <Typography variant="body1">Loading AI Talker configuration...</Typography>
      ) : (
        <>
          <Box className="playground-waves">
            <AnimatedWaves 
              isSTTActive={isSTTActive} 
              isTTSPlaying={isTTSPlaying} 
              isRedButtonClicked={isRedButtonClicked}
            />
          </Box>
          <Box className="playground-content" sx={{ marginBottom: '20px' }}>
            <Typography variant="body1" className="playground-text">
              You said: {transcript || '(Waiting for speech input...)'}
            </Typography>
            {aiResponse && (
              <Typography variant="body1" className="playground-text">
                AI response: {aiResponse}
              </Typography>
            )}
            {isTTSPlaying && (
              <Typography variant="body1" className="playground-text">
                AI is speaking...
              </Typography>
            )}
          </Box>
          <OnboardingButtonDock
            onSave={toggleListening}
            onBack={() => {
              dispatch(setCurrentStep(2)); // Go back to AI Talker Configuration
            }}
            onNext={() => {
              dispatch(setCurrentStep(4)); // Move to Integration
            }}
            title={
              isSTTActive ? (
                <>
                  <MicOffIcon /> Stop Listening
                </>
              ) : isTTSPlaying ? (
                "Speaking..."
              ) : (
                <>
                  <MicIcon /> Start Listening
                </>
              )
            }
            loading={isTTSPlaying || isLoading}
            desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
            mobileClassName="fixed bottom-8 right-8 z-50"
          />
        </>
      )}
    </Box>
  );
};

export default OnboardingPlayground;
